import React from 'react';
import { Box, Typography, Paper, useMediaQuery, useTheme, Chip, Button, IconButton } from '@mui/material';
import { LocationOn, Home, AttachMoney, SquareFoot, Layers, MeetingRoom, Edit } from '@mui/icons-material';


const PropertyCard = ({ title, city, street, district, location, price, type, image_url, area, onClick, rooms, floor, total_floors, rent, renderAction, renderBottomAction, created_at, is_active = true, onEdit, showEditButton = false, is_premium = false }) => {
    // Calculate total price (czynsz + wynajem)
    // rent = czynsz, price = wynajem
    const czynsz = Number(rent) || 0;
    const wynajem = Number(price) || 0;
    const totalPrice = czynsz + wynajem;
    const displayPriceFormatted = totalPrice > 0 ? `${totalPrice.toLocaleString('pl-PL')} zł/mies.` : '— zł/mies.';
    
    // Format date
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };
    
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: 'white',
        transition: 'box-shadow 0.3s ease',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row', // Kolumna na mobile, wiersz na desktop
        alignItems: 'stretch',
        mb: 2,
        width: '100%',
        minHeight: isMobile ? 'auto' : 170, // Auto wysokość na mobile
        boxSizing: 'border-box',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
        }
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          width: isMobile ? '100%' : 260, // Pełna szerokość na mobile
          minWidth: isMobile ? 'auto' : 260,
          height: isMobile ? 200 : 'auto', // Na desktop rozciąga się do wysokości karty
          minHeight: isMobile ? 'auto' : 140,
          alignSelf: 'stretch',
          background: image_url ? `url(${image_url}) center/cover no-repeat` : 'linear-gradient(135deg, #14B8A6 0%, #0891B2 100%)',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {/* Akcje w prawym górnym rogu zdjęcia na mobile */}
        {isMobile && (renderAction || (showEditButton && onEdit)) && (
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, display: 'flex', gap: 1.5 }}>
            {/* Przycisk edycji */}
            {showEditButton && onEdit && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)', // Białe tło na mobile
                  color: '#FF8C00',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    color: '#E67E00'
                  }
                }}
              >
                <Edit sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            {/* Przycisk usuwania - renderAction musi mieć style z białym tłem */}
            {renderAction && (
              <Box sx={{
                '& .MuiIconButton-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9) !important',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1) !important'
                  }
                }
              }}>
                {renderAction()}
              </Box>
            )}
          </Box>
        )}
        {/* Label Wyróżnione dla premium */}
        {is_premium && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: '2px',
              backgroundColor: 'rgba(248, 223, 99, 0.88)',
              backdropFilter: 'blur(6px)',
              color: '#111',
              fontWeight: 700,
              fontSize: '0.7rem',
              px: 1.5,
              py: 0.4,
              borderRadius: '6px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              zIndex: 5,
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
          >
            Promowane
          </Box>
        )}
      </Box>
      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative' }}>
        {/* Akcje w prawym górnym rogu tekstów tylko na desktop */}
        {!isMobile && (renderAction || (showEditButton && onEdit)) && (
          <Box sx={{ position: 'absolute', top: 8, right: 4, zIndex: 10, display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {/* Przycisk edycji */}
            {showEditButton && onEdit && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                sx={{
                  backgroundColor: 'rgba(255, 140, 0, 0.1)',
                  color: '#FF8C00',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 140, 0, 0.2)',
                    color: '#E67E00',
                    marginRight: '4px'
                  }
                }}
              >
                <Edit sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            {/* Przycisk usuwania */}
            {renderAction && renderAction()}
          </Box>
        )}
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 'bold', 
            mb: 0.5, 
            fontSize: isMobile ? '0.9rem' : '0.875rem', 
            pr: renderAction ? 10 : 2,
            wordWrap: 'break-word',
            hyphens: 'auto',
            lineHeight: 1.3,
            maxWidth: '100%',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
          <LocationOn sx={{ color: '#D2691E', fontSize: 16, mr: 0.5 }} />
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#666', 
              fontSize: isMobile ? '0.7rem' : '0.8rem', 
              pr: renderAction ? 10 : 2,
              wordWrap: 'break-word',
              lineHeight: 1.2,
              maxWidth: '100%',
              overflow: 'hidden'
            }}
          >
            {city}
            {street && `, ${street.trim()}`}
            {district && `, ${district.trim()}`}
          </Typography>
        </Box>
        {/* Wrapper z pozycją relative — zawartość zawsze zajmuje miejsce */}
        <Box sx={{ position: 'relative' }}>
          {/* Normalna zawartość — ukryta (visibility) gdy tryb usuwania, ale nadal zajmuje miejsce */}
          <Box sx={{ visibility: renderBottomAction ? 'hidden' : 'visible' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
              <Chip
                icon={<MeetingRoom sx={{ color: '#D2691E' }} />}
                label={rooms === 0 ? 'Pokój' : rooms === 1 ? 'Kawalerka' : rooms ? `${rooms} ${rooms <= 4 ? 'pokoje' : 'pokoi'}` : '— pokoje'}
                size="small"
                sx={{ backgroundColor: '#F7FAFC', color: '#4A5568', fontSize: '0.7rem' }}
              />
              <Chip
                icon={<SquareFoot sx={{ color: '#D2691E' }} />}
                label={area ? `${area} m²` : '— m²'}
                size="small"
                sx={{ backgroundColor: '#F7FAFC', color: '#4A5568', fontSize: '0.7rem' }}
              />
              <Chip
                icon={<Layers sx={{ color: '#D2691E' }} />}
                label={floor !== undefined && total_floors !== undefined ? `piętro ${floor}/${total_floors}` : floor !== undefined ? `piętro ${floor}` : 'piętro —'}
                size="small"
                sx={{ backgroundColor: '#F7FAFC', color: '#4A5568', fontSize: '0.7rem' }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', pr: renderAction ? 10 : 2 }}>
              <AttachMoney sx={{ color: '#FB923C', fontSize: 18, mr: 0.5, flexShrink: 0 }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666', 
                  fontWeight: 'bold', 
                  mr: 1,
                  fontSize: isMobile ? '0.75rem' : '0.85rem',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Cena całkowita:
              </Typography>
              <Typography 
                variant="body1"
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#FB923C', 
                  fontSize: isMobile ? '0.95rem' : '1rem',
                  whiteSpace: 'nowrap'
                }}
              >
                {displayPriceFormatted}
              </Typography>
            </Box>
            {created_at && (
              <Chip
                label={`Opublikowano: ${formatDate(created_at)}`}
                size="small"
                sx={{ 
                  mt: 1,
                  backgroundColor: is_active ? '#E8F5E9' : '#FFEBEE',
                  color: is_active ? '#2E7D32' : '#C62828',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  '& .MuiChip-label': {
                    px: 2,
                    py: 0.5
                  }
                }}
              />
            )}
          </Box>
          {/* Pasek usuwania — absolutnie na wierzchu, wyśrodkowany w pionie */}
          {renderBottomAction && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '100%' }}>{renderBottomAction()}</Box>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

export default PropertyCard;